import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../config/api';
import { extractErrorMessage } from '../utils/extractError';

interface ReportItem {
  id: number;
  title: string;
  report_date: string;
  report_type: string;
  status: string;
  monitor_name?: string;
  created_at: string;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatReportType(type: string): string {
  return type.charAt(0).toUpperCase() + type.slice(1);
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  draft: { label: 'Draft', color: 'var(--status-warning)', bg: 'var(--status-warning-muted)' },
  published: { label: 'Published', color: 'var(--status-success)', bg: 'var(--status-success-muted)' },
};

export default function ReportListPage() {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();

  const storeName = (window.history.state?.usr?.storeName as string) || `Store #${storeId}`;

  const [reports, setReports] = useState<ReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchReports = useCallback(async () => {
    try {
      const res = await api.get(`/stores/${storeId}/reports`);
      setReports(res.data.items || res.data.reports || []);
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load reports.'));
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        <button onClick={() => navigate(`/stores/${storeId}`)} style={styles.backButton}>
          ← Back to Store
        </button>

        <div style={styles.pageHeader}>
          <div>
            <h2 style={styles.pageTitle}>Reports</h2>
            <p style={styles.subtitle}>{storeName}</p>
          </div>
          <Link to={`/stores/${storeId}/create-report`} style={styles.createButton}>
            + Create Report
          </Link>
        </div>

        {error && <div style={styles.errorBanner}>{error}</div>}

        {loading ? (
          <div style={styles.placeholder}>
            <p style={styles.placeholderText}>Loading reports...</p>
          </div>
        ) : reports.length === 0 ? (
          <div style={styles.placeholder}>
            <p style={styles.placeholderText}>No reports yet for this store.</p>
          </div>
        ) : (
          <div style={styles.reportList}>
            {reports.map((report) => {
              const sConfig = STATUS_CONFIG[report.status] || STATUS_CONFIG.draft;
              return (
                <div
                  key={report.id}
                  style={styles.reportCard}
                  onClick={() => navigate(`/reports/${report.id}`)}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-surface-raised)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'var(--bg-surface)'; }}
                >
                  <div style={styles.reportCardTop}>
                    <span style={{
                      ...styles.statusBadge,
                      background: sConfig.bg,
                      color: sConfig.color,
                    }}>
                      {sConfig.label}
                    </span>
                    <span style={styles.reportType}>{formatReportType(report.report_type)}</span>
                    <span style={styles.reportDate}>{formatDate(report.report_date)}</span>
                  </div>
                  <h3 style={styles.reportTitle}>{report.title}</h3>
                  {report.monitor_name && (
                    <p style={styles.monitorName}>By {report.monitor_name}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { minHeight: '100vh', background: 'var(--bg-base)' },
  main: { maxWidth: 960, margin: '0 auto', padding: 'var(--space-8) var(--space-6)' },
  backButton: {
    background: 'none', border: 'none', color: 'var(--accent-text)',
    fontSize: 'var(--text-md)', fontWeight: 500, fontFamily: 'var(--font-body)', cursor: 'pointer', padding: 0, marginBottom: 'var(--space-6)',
  },
  pageHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 'var(--space-6)',
  },
  pageTitle: { fontSize: 'var(--text-2xl)', fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--text-primary)', margin: 0 },
  subtitle: { fontSize: 'var(--text-md)', fontFamily: 'var(--font-body)', color: 'var(--text-secondary)', margin: '4px 0 0' },
  createButton: {
    display: 'inline-flex', alignItems: 'center', padding: '10px 20px',
    borderRadius: 'var(--radius-lg)', background: 'var(--accent)', color: '#fff',
    fontSize: 'var(--text-md)', fontWeight: 600, fontFamily: 'var(--font-body)', textDecoration: 'none',
  },
  errorBanner: {
    background: 'var(--status-critical-muted)', color: 'var(--status-critical)', padding: '12px 16px',
    borderRadius: 'var(--radius-lg)', fontSize: 'var(--text-base)', fontFamily: 'var(--font-body)',
    border: '1px solid rgba(255,71,87,0.3)', marginBottom: 'var(--space-5)',
  },
  placeholder: {
    background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl)', border: '1px solid var(--border-subtle)',
    padding: '48px 24px', textAlign: 'center',
  },
  placeholderText: { color: 'var(--text-secondary)', fontSize: 'var(--text-md)', fontFamily: 'var(--font-body)', margin: 0 },
  reportList: { display: 'flex', flexDirection: 'column', gap: 12 },
  reportCard: {
    background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border-subtle)',
    padding: '20px 24px', cursor: 'pointer', transition: 'background 0.15s',
  },
  reportCardTop: {
    display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10,
  },
  statusBadge: {
    padding: '2px 8px', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-xs)', fontWeight: 700,
    fontFamily: 'var(--font-body)', letterSpacing: 0.5,
  },
  reportType: {
    fontSize: 'var(--text-sm)', fontWeight: 600, fontFamily: 'var(--font-body)', color: 'var(--text-secondary)',
    background: 'var(--bg-surface-raised)', padding: '2px 8px', borderRadius: 'var(--radius-sm)',
  },
  reportDate: { marginLeft: 'auto', fontSize: 'var(--text-sm)', fontFamily: 'var(--font-mono)', color: 'var(--text-tertiary)' },
  reportTitle: { fontSize: 'var(--text-md)', fontWeight: 600, fontFamily: 'var(--font-body)', color: 'var(--text-primary)', margin: '0 0 4px' },
  monitorName: { fontSize: 'var(--text-sm)', fontFamily: 'var(--font-body)', color: 'var(--text-tertiary)', margin: 0 },
};
